import { useCallback, useMemo, useState } from 'react'

export type ViewMode = 'grid' | 'list'
export type FilterType = 'all' | 'parent' | 'branch' | 'leaf'

export interface SortOption {
  label: string
  key: string
  getValue: (model: any) => number
}

export function useModelFilters(initialView: ViewMode = 'grid') {
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [sortBy, setSortBy] = useState<string>('date')
  const [viewMode, setViewMode] = useState<ViewMode>(initialView)

  const handleSearchChange = useCallback((value: string) => setSearch(value), [])
  const handleFilterTypeChange = useCallback((value: FilterType) => setFilterType(value), [])
  const handleSortChange = useCallback((key: string) => setSortBy(key), [])
  const handleViewModeChange = useCallback((mode: ViewMode) => setViewMode(mode), [])

  return {
    search,
    setSearch: handleSearchChange,
    filterType,
    setFilterType: handleFilterTypeChange,
    sortBy,
    setSortBy: handleSortChange,
    viewMode,
    setViewMode: handleViewModeChange,
  }
}

export function useApplyFilters(nodes: any[], search: string, filterType: FilterType) {
  return useMemo(() => {
    let filtered = nodes
    if (search.trim()) {
      filtered = filtered.filter((node) =>
        node.model.name.toLowerCase().includes(search.trim().toLowerCase())
      )
    }
    if (filterType !== 'all') {
      filtered = filtered.filter((node) => {
        switch (filterType) {
          case 'parent':
            return !node.parent_id
          case 'branch':
            return node.parent_id && node.children && node.children.length > 0
          case 'leaf':
            return node.parent_id && (!node.children || node.children.length === 0)
          default:
            return true
        }
      })
    }
    return filtered
  }, [nodes, search, filterType])
}

export function useSort(nodes: any[], sortBy: string, getSortOptions: () => SortOption[]) {
  return useMemo(() => {
    const selectedOption = getSortOptions().find((opt) => opt.key === sortBy)
    if (!selectedOption) return nodes
    return [...nodes].sort(
      (a, b) => selectedOption.getValue(b.model) - selectedOption.getValue(a.model)
    )
  }, [nodes, sortBy, getSortOptions])
}
